import {
  Accordion,
  AccordionItem,
  Loading,
  Modal,
} from "@appkit4/react-components";
import { useEffect, useMemo, useState } from "react";
import DataImportModal from "../../components/DataImportModal/DataImportModal";
import toast from "../../components/DismissibleToast";
import {
  Button,
  Input,
  InputNumber,
} from "../../components/ReadonlyAwareInputs";
import { getPageTooltips } from "../../services/GroupContext";
import { setQNGIETotals } from "../../services/calculations/GroupDataCalcs";
import GroupDataTable from "./GroupDataTable";
import {
  WarningModal,
  handleCancel,
  renderPrimaryAccordionTitle,
  renderSummary,
  sumArray,
} from "./groupDataUtil";
import { useGroupDataRows } from "./utils/groupDataUtil";
import { getQngieRowConfig } from "./utils/qngieUtil";

const QNGIE = ({
  setVisible,
  currentPeriod,
  setCurrentPeriod,
  save,
  errorState: [isError, setIsError],
  isParentError,
  isReadOnly,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const tooltips = useMemo(() => getPageTooltips("qngieModal"), []);
  const qngieRowConfig = useMemo(() => getQngieRowConfig(tooltips), [tooltips]);
  const tables = useGroupDataRows(qngieRowConfig);

  const downwardAdjustments = [
    tables["expenses_related_party"]?.state[0],
    tables["expenses_related_financial_assistance"]?.state[0],
    tables["expenses_dependent_securities"]?.state[0],
    tables["expenses_equity_notes"]?.state[0],
    tables["specialist_regimes_adjustments"]?.state[0],
    tables["miscellaneous_adjustments"]?.state[0],
  ];

  const infraCompanyAdjustments = [
    tables["exclude_qic_income_exempt_s440_s442_2b"]?.state[0],
    tables["exclude_qic_expense_exempt_s438_s442_2b"]?.state[0],
  ];

  const [activeKeys, setActiveKeys] = useState("");
  const [qngieSubTotal, setQngieSubTotal] = useState(0);
  const [angieBeforeAdjustments, setAngieBeforeAnyAdjustments] = useState(
    currentPeriod?.angie_amount_before_any_adjustments
      ? currentPeriod?.angie_amount_before_any_adjustments
      : 0,
  );
  const onClickAccordion = (activeKeys) => {
    setActiveKeys(activeKeys);
  };

  useEffect(() => {
    let total =
      sumArray(downwardAdjustments) +
      sumArray([
        tables["qngie_proportion_associated_groups_s427_5"]?.state[0],
      ]) +
      sumArray(infraCompanyAdjustments);
    setQngieSubTotal(total);
  }, [
    downwardAdjustments,
    tables["qngie_proportion_associated_groups_s427_5"]?.state[0],
    infraCompanyAdjustments,
  ]);

  const calculateQNGIE = () => {
    let total =
      sumArray(downwardAdjustments) +
      sumArray([
        tables["qngie_proportion_associated_groups_s427_5"]?.state[0],
      ]) +
      sumArray(infraCompanyAdjustments);
    if (currentPeriod?.angie_amount_before_any_adjustments) {
      total += currentPeriod?.angie_amount_before_any_adjustments;
    }
    return Math.round(total);
  };

  const updateQngieValues = (qngie) => {
    const newPeriod = {
      ...currentPeriod,
      qngie,
    };

    setQNGIETotals(newPeriod);

    return newPeriod;
  };

  const submit = () => {
    const qngie = Object.keys(tables)?.reduce((acc, k) => {
      acc[k] = tables[k]?.state[0];
      return acc;
    }, {});

    const newPeriod = updateQngieValues(qngie);

    setCurrentPeriod(newPeriod);
    const promise = save(newPeriod);
    toast.promise(promise, {
      loading: "Saving QNGIE data...",
      success: () => {
        setVisible(false);
        return "Saved QNGIE data";
      },
      error: "Something went wrong saving QNGIE data",
    });
  };

  useEffect(() => {
    setIsError(Object.keys(tables)?.some((k) => tables[k]?.error[0]));
  }, [Object.keys(tables)?.map((k) => tables[k]?.error[0])]);

  useEffect(() => {
    const qngie = currentPeriod?.qngie;
    if (!qngie) {
      return;
    }

    Object.keys(tables)?.forEach((key) => {
      tables[key]?.state[1](qngie[key]);
    });
  }, []);

  const [isModalChanged, setIsModalChanged] = useState(false);

  useEffect(() => {
    let total = calculateQNGIE();
    if (
      currentPeriod?.local_curr_qualifying_net_group_interest_expense !== total
    ) {
      setIsModalChanged(true);
    } else {
      setIsModalChanged(false);
    }
  }, [calculateQNGIE()]);

  const [showWarningModal, setShowWarningModal] = useState(false);

  const onImportSuccess = (qngie) => {
    const isPlainObject = (val) =>
      typeof val === "object" && val !== null && val.constructor === Object;

    if (isPlainObject(qngie)) {
      updateQngieValues(qngie);
      Object.entries(tables)?.forEach(([key, row]) => {
        row.state[1](qngie[key]);
      });
    } else {
      console.error("error setting values");
      toast.error("Error Importing QNGIE");
    }
  };

  const handleExitWithoutSaving = () => {
    setIsError(false);
    setVisible(false);
  };

  if (isLoading) {
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      ></Loading>
    );
  }
  return (
    <>
      <Modal
        initialFocus={false}
        visible={true}
        title={`Qualifying Net Group-Interest Expense`}
        onCancel={() =>
          handleCancel(
            isModalChanged,
            isReadOnly,
            setVisible,
            setShowWarningModal,
            setIsError,
          )
        }
        maskCloseable={false}
        modalStyle={{ width: "90%", maxHeight: "90%" }}
        bodyStyle={{ maxHeight: "90%" }}
      >
        <table className="table">
          <tbody>
            <tr>
              <td>
                <div className="row">
                  <p className="col-1">Selected input value currency:</p>
                  <Input
                    className="col"
                    disabled
                    value={currentPeriod?.currency}
                  />
                </div>
              </td>
              <td>
                <div className="row">
                  <p className="col-4">
                    Entered FX rate: 1 GBP = X Local Currency (if required):
                  </p>
                  <InputNumber
                    className="col"
                    value={currentPeriod?.fx_rate}
                    disabled
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="row group-data-modal-top-input-header">
          <p>
            Changes to the currency and exchange rate can be made in the Net
            group-interest expense section
          </p>
        </div>

        {isError && (
          <div className="err">
            Description missing - a description must be provided for every
            value.
          </div>
        )}

        <Accordion
          multiple={false}
          onClick={onClickAccordion}
          activeKeys={activeKeys}
        >
          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "ANGIE before Interest Allowance (non-consolidated investment) and QIC elections",
              angieBeforeAdjustments,
              tooltips.angieBeforeAdjustments,
            )}
            itemKey="1"
          ></AccordionItem>
          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Downward Adjustments",
              sumArray(downwardAdjustments),
              tooltips.downwardAdjustments,
            )}
            itemKey="2"
          >
            {GroupDataTable(tables, [
              "expenses_related_party",
              "expenses_related_financial_assistance",
              "expenses_dependent_securities",
              "expenses_equity_notes",
              "specialist_regimes_adjustments",
              "miscellaneous_adjustments",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Interest Allowance (Non-Consolidated Investment) Election",
              sumArray([
                tables["qngie_proportion_associated_groups_s427_5"]?.state[0],
              ]),
              tooltips.nonConsolidatedElection,
            )}
            itemKey="3"
          >
            {GroupDataTable(tables, [
              "qngie_proportion_associated_groups_s427_5",
            ])}
          </AccordionItem>

          <AccordionItem
            title={renderPrimaryAccordionTitle(
              "Adjustments for Qualifying Infrastructure Companies",
              sumArray(infraCompanyAdjustments),
              tooltips.adjustmentsForQualifyingCompanies,
            )}
            itemKey="4"
          >
            {GroupDataTable(tables, [
              "exclude_qic_income_exempt_s440_s442_2b",
              "exclude_qic_expense_exempt_s438_s442_2b",
            ])}
          </AccordionItem>
        </Accordion>

        <h3>Summary</h3>

        {renderSummary(
          currentPeriod?.currency,
          currentPeriod?.fx_rate,
          qngieSubTotal + angieBeforeAdjustments,
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "end",
          }}
        >
          <DataImportModal pageName="qngie" onSuccess={onImportSuccess} />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "end",
              paddingTop: "30px",
              gap: 10,
            }}
          >
            <Button
              neverReadonly
              onClick={() =>
                handleCancel(
                  isModalChanged,
                  isReadOnly,
                  setVisible,
                  setShowWarningModal,
                  setIsError,
                )
              }
              kind="secondary"
            >
              Cancel
            </Button>
            <Button
              hiddenInReadonly
              disabled={isError || isParentError}
              onClick={submit}
            >
              Submit
            </Button>
          </div>
        </div>
      </Modal>

      <WarningModal
        visible={showWarningModal}
        setIsVisible={setShowWarningModal}
        callBackFunc={handleExitWithoutSaving}
      ></WarningModal>
    </>
  );
};

export default QNGIE;
